import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { SelectedApp } from "@/components/applications/ApplicationCatalog";
import { Button } from "@/components/ui/button";
import { useApplications } from "@/hooks/useApplications";
import { useAppConfigurations } from "@/hooks/useAppConfigurations";
import { Application } from "@/types/application";
import { toast } from "sonner";
import { ArrowLeft, ChevronDown, Save, Send } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ApplicationWizardSteps, ApplicationWizardStep } from "@/components/applications/ApplicationWizardSteps";
import { SelectAppStep } from "@/components/applications/steps/SelectAppStep";
import { AppAssignmentStep } from "@/components/applications/steps/AppAssignmentStep";
import { AppConfigurationStep, InitialConfiguration } from "@/components/applications/steps/AppConfigurationStep";

export default function AddApplicationPage() {
  const navigate = useNavigate();
  const { addApplication } = useApplications();
  const { createConfiguration } = useAppConfigurations();
  const [currentStep, setCurrentStep] = useState<ApplicationWizardStep>("select");
  const [completedSteps, setCompletedSteps] = useState<ApplicationWizardStep[]>([]);
  const [selectedApp, setSelectedApp] = useState<SelectedApp | null>(null);
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [abmCustomer, setAbmCustomer] = useState<{ id: string; name: string } | null>(null);
  const [initialConfiguration, setInitialConfiguration] = useState<InitialConfiguration | null>(null);
  
  const steps: ApplicationWizardStep[] = ["select", "assignment", "configuration"];
  const isAbmApp = selectedApp?.type === 'abm';

  // ABM customer lookup is no longer backed by Supabase; skip for now
  useEffect(() => {
    if (selectedApp?.type !== 'abm') { setAbmCustomer(null); }
  }, [selectedApp]);

  const handleSelectApp = (selection: SelectedApp) => {
    setSelectedApp(selection);
    if (selection.type !== 'abm') { setSelectedCustomers([]); }
    setSelectedGroups([]); setSelectedTags([]);
  };

  const getSelectedAppId = (): string | undefined => {
    if (!selectedApp) return undefined;
    return selectedApp.type === 'appStore' ? selectedApp.app.appStoreId : selectedApp.app.id;
  };

  const canProceedFromStep = (step: ApplicationWizardStep): boolean => {
    switch (step) { case "select": return selectedApp !== null; case "assignment": return selectedCustomers.length > 0; case "configuration": return true; default: return false; }
  };

  const handleNext = () => {
    if (!canProceedFromStep(currentStep)) { if (currentStep === "select") toast.error("Please select an application"); return; }
    if (!completedSteps.includes(currentStep)) setCompletedSteps(prev => [...prev, currentStep]);
    const ci = steps.indexOf(currentStep);
    if (ci < steps.length - 1) setCurrentStep(steps[ci + 1]);
  };

  const handleBack = () => { const ci = steps.indexOf(currentStep); if (ci > 0) setCurrentStep(steps[ci - 1]); else navigate('/applications'); };

  const handleAddApplication = async (publish: boolean = false) => {
    if (!selectedApp || selectedCustomers.length === 0) { toast.error("Please select an application and at least one customer"); return; }
    try {
      let newApp: Omit<Application, "id">;
      const status = publish ? 'published' : 'draft';
      if (selectedApp.type === 'appStore') {
        const app = selectedApp.app;
        newApp = { name: app.name, icon: app.iconLarge || app.icon, description: app.description, version: app.version, size: app.size, releaseDate: app.releaseDate.split('T')[0], source: 'appStore', platform: app.platform, bundleId: app.bundleId, categories: app.categories, customers: selectedCustomers, status, appStoreId: app.appStoreId, appStoreUrl: app.appStoreUrl, countryCode: 'US', price: app.price, minimumOsVersion: app.minimumOsVersion, developer: app.developer, rating: app.rating };
      } else {
        const app = selectedApp.app;
        newApp = { name: app.name, icon: app.icon, version: app.version, size: 'Unknown', releaseDate: new Date().toISOString().split('T')[0], source: 'abm', platform: app.platform, bundleId: app.bundleId, customers: selectedCustomers, status, abmConnectionId: app.abmConnectionId, licenseTotal: app.licenseTotal, licenseUsed: app.licenseUsed };
      }
      const createdApp = await addApplication(newApp);
      if (initialConfiguration) {
        await createConfiguration({ applicationId: createdApp.id, name: initialConfiguration.name, description: initialConfiguration.description, configurationData: { keys: initialConfiguration.keys }, customers: selectedCustomers, isActive: true });
      }
      toast.success(publish ? "Application added and published" : "Application saved as draft");
      navigate('/applications');
    } catch (error) { console.error("Failed to add application:", error); toast.error("Failed to add application"); }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case "select": return <SelectAppStep onSelectApp={handleSelectApp} selectedAppId={getSelectedAppId()} />;
      case "assignment": return selectedApp ? <AppAssignmentStep selectedApp={selectedApp} customers={selectedCustomers} groups={selectedGroups} tags={selectedTags} setCustomers={setSelectedCustomers} setGroups={setSelectedGroups} setTags={setSelectedTags} isAbmApp={isAbmApp} preSelectedCustomer={abmCustomer} /> : null;
      case "configuration": return <AppConfigurationStep configuration={initialConfiguration} onConfigurationChange={setInitialConfiguration} />;
      default: return null;
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between px-6 py-4 border-b bg-background">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleBack}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
          <h1 className="text-xl font-semibold">Add Application</h1>
        </div>
        <div className="flex items-center gap-2">
          {currentStep !== "select" && <Button variant="outline" onClick={handleBack}>Previous</Button>}
          {currentStep === "select" ? <Button onClick={handleNext} disabled={!canProceedFromStep("select")}>Continue</Button>
            : currentStep === "assignment" ? <Button onClick={handleNext} disabled={!canProceedFromStep("assignment")}>Continue</Button>
            : <DropdownMenu><DropdownMenuTrigger asChild><Button>Add Application<ChevronDown className="ml-2 h-4 w-4" /></Button></DropdownMenuTrigger><DropdownMenuContent align="end" className="w-48"><DropdownMenuItem onClick={() => handleAddApplication(false)}><Save className="mr-2 h-4 w-4" />Save as Draft</DropdownMenuItem><DropdownMenuItem onClick={() => handleAddApplication(true)}><Send className="mr-2 h-4 w-4" />Add and Publish</DropdownMenuItem></DropdownMenuContent></DropdownMenu>}
        </div>
      </div>
      <ApplicationWizardSteps currentStep={currentStep} completedSteps={completedSteps} />
      <div className="flex-1 overflow-auto"><div className="p-6">{renderStepContent()}</div></div>
    </div>
  );
}
